import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  Paperclip,
  UserPlus,
  Trash,
  MapPin,
  Clock,
  Phone,
  EnvelopeSimple,
} from "@phosphor-icons/react";
import { AppHeader } from "@/components/AppHeader";
import { DraftReply } from "@/components/DraftReply";
import { api, API, formatApiErrorDetail } from "@/lib/api";

const STATUS_STYLE = {
  New: "border-blue-500/40 bg-blue-500/10 text-blue-300",
  Contacted: "border-indigo-500/40 bg-indigo-500/10 text-indigo-300",
  Quoted: "border-amber-500/40 bg-amber-500/10 text-amber-300",
  Won: "border-emerald-500/40 bg-emerald-500/10 text-emerald-300",
  Lost: "border-red-500/40 bg-red-500/10 text-red-300",
};

const micro =
  "font-mono-data text-[10px] uppercase tracking-[0.22em] text-zinc-500";

const Fact = ({ icon: Icon, label, value }) =>
  value ? (
    <div className="flex items-start gap-3">
      <Icon size={15} weight="bold" className="mt-0.5 shrink-0 text-zinc-600" />
      <div className="min-w-0">
        <div className={micro}>{label}</div>
        <div className="mt-0.5 break-words text-sm text-zinc-200">{value}</div>
      </div>
    </div>
  ) : null;

export default function LeadDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [statuses, setStatuses] = useState([]);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const token = localStorage.getItem("sw_token");

  const load = useCallback(async () => {
    try {
      const [{ data }, meta] = await Promise.all([
        api.get(`/leads/${id}`),
        api.get("/leads/meta"),
      ]);
      setLead(data);
      setNotes(data.notes || "");
      setStatuses(meta.data.statuses || []);
    } catch {
      toast.error("That request could not be opened");
      navigate("/leads");
    }
  }, [id, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const patch = async (body) => {
    const { data } = await api.put(`/leads/${id}`, body);
    setLead(data);
    toast.success("Request updated");
  };

  const convert = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/leads/${id}/convert`);
      toast.success(data.already ? "Already an account" : "Account created");
      navigate(`/customers/${data.customer_id}`);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || "Could not convert",
      );
    }
    setBusy(false);
  };

  const remove = async () => {
    if (!window.confirm(`Delete the request from ${lead.name}?`)) return;
    await api.delete(`/leads/${id}`);
    toast.success("Request deleted");
    navigate("/leads");
  };

  if (!lead)
    return (
      <div className="min-h-screen bg-zinc-950">
        <AppHeader title="Quote request" />
      </div>
    );

  const files = (lead.files || []).filter((f) => !f.is_deleted);

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100">
      <AppHeader title="Quote request" />
      <main className="mx-auto max-w-5xl px-5 pb-24 pt-6">
        <Link
          to="/leads"
          data-testid="lead-detail-back"
          className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-widest text-zinc-500 transition-colors duration-150 hover:text-zinc-200"
        >
          <ArrowLeft size={13} weight="bold" /> All leads
        </Link>

        <div className="mt-4 rounded-lg border border-zinc-800 bg-zinc-900 p-5">
          <div className="flex flex-wrap items-center gap-3">
            <h1
              className="text-2xl font-bold tracking-tight"
              data-testid="lead-detail-name"
            >
              {lead.name}
            </h1>
            <span
              data-testid="lead-detail-status"
              className={`rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest ${
                STATUS_STYLE[lead.status] || "border-zinc-700 text-zinc-300"
              }`}
            >
              {lead.status}
            </span>
            <span className="font-mono-data text-xs text-zinc-600">
              {lead.code}
            </span>
            <span className="rounded-full border border-zinc-800 px-2.5 py-1 text-[10px] uppercase tracking-widest text-zinc-400">
              {lead.source}
            </span>
            <div className="ml-auto flex flex-wrap gap-2">
              <select
                data-testid="lead-detail-status-select"
                value={lead.status}
                onChange={(e) => patch({ status: e.target.value })}
                className="h-10 rounded-md border border-zinc-800 bg-zinc-950 px-3 text-xs text-zinc-200"
              >
                {statuses.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
              {lead.customer_id ? (
                <Link
                  to={`/customers/${lead.customer_id}`}
                  data-testid="lead-detail-open-account"
                  className="flex h-10 items-center gap-2 rounded-md border border-zinc-800 px-3 text-[10px] font-bold uppercase tracking-widest text-zinc-300 hover:border-indigo-500/40 hover:text-white"
                >
                  <UserPlus size={14} weight="bold" /> Open account
                </Link>
              ) : (
                <button
                  type="button"
                  data-testid="lead-detail-convert"
                  disabled={busy}
                  onClick={convert}
                  className="flex h-10 items-center gap-2 rounded-md bg-indigo-500 px-4 text-[10px] font-bold uppercase tracking-widest text-white hover:bg-indigo-400 disabled:opacity-40"
                >
                  <UserPlus size={14} weight="bold" /> Make an account
                </button>
              )}
              <button
                type="button"
                data-testid="lead-detail-delete"
                onClick={remove}
                className="flex h-10 items-center gap-2 rounded-md border border-zinc-800 px-3 text-[10px] font-bold uppercase tracking-widest text-zinc-400 hover:border-red-500/40 hover:text-red-400"
              >
                <Trash size={14} weight="bold" /> Delete
              </button>
            </div>
          </div>

          <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            <Fact icon={Phone} label="Phone" value={lead.phone} />
            <Fact icon={EnvelopeSimple} label="Email" value={lead.email} />
            <Fact icon={MapPin} label="Job address" value={lead.address} />
            <Fact
              icon={Clock}
              label="Project"
              value={`${lead.project_type} · ${lead.timeline}${
                lead.budget ? ` · ${lead.budget}` : ""
              }`}
            />
            <Fact
              icon={UserPlus}
              label="Working with"
              value={lead.working_with}
            />
            <Fact
              icon={Clock}
              label="Came in"
              value={(lead.created_at || "").slice(0, 16).replace("T", " ")}
            />
          </div>

          {(lead.colors?.length || lead.other_colors) && (
            <div className="mt-6">
              <div className={micro}>Colors they like</div>
              <div
                className="mt-2 flex flex-wrap gap-1.5"
                data-testid="lead-detail-colors"
              >
                {(lead.colors || []).map((c) => (
                  <span
                    key={c}
                    className="rounded-full border border-indigo-400/30 bg-indigo-500/10 px-2.5 py-1 text-[11px] text-indigo-200"
                  >
                    {c}
                  </span>
                ))}
                {lead.other_colors && (
                  <span className="rounded-full border border-zinc-700 px-2.5 py-1 text-[11px] text-zinc-400">
                    {lead.other_colors}
                  </span>
                )}
              </div>
            </div>
          )}

          {lead.message && (
            <div className="mt-6">
              <div className={micro}>What they told us</div>
              <p
                className="mt-2 whitespace-pre-wrap rounded-lg border border-zinc-800 bg-zinc-950 p-4 text-sm leading-relaxed text-zinc-300"
                data-testid="lead-detail-message"
              >
                {lead.message}
              </p>
            </div>
          )}

          {files.length > 0 && (
            <div className="mt-6">
              <div className={micro}>Photos and plans they sent</div>
              <div
                className="mt-2 grid gap-3 sm:grid-cols-2 lg:grid-cols-3"
                data-testid="lead-detail-files"
              >
                {files.map((f) => {
                  const href = `${API}/leads/file/${f.storage_path}?auth=${token}`;
                  const isImg = (f.content_type || "").startsWith("image/");
                  return (
                    <a
                      key={f.id}
                      href={href}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`lead-detail-file-${f.id}`}
                      className="group overflow-hidden rounded-lg border border-zinc-800 bg-zinc-950 transition-colors duration-150 hover:border-indigo-400/50"
                    >
                      {isImg ? (
                        <img
                          src={href}
                          alt={f.name}
                          className="h-40 w-full object-cover opacity-90 transition-opacity duration-200 group-hover:opacity-100"
                        />
                      ) : (
                        <div className="flex h-40 items-center justify-center">
                          <Paperclip size={22} className="text-zinc-600" />
                        </div>
                      )}
                      <div className="flex items-center gap-2 px-3 py-2">
                        <Paperclip
                          size={12}
                          weight="bold"
                          className="shrink-0 text-zinc-600"
                        />
                        <span className="min-w-0 flex-1 truncate text-xs text-zinc-300">
                          {f.name}
                        </span>
                        <span className="font-mono-data shrink-0 text-[10px] text-zinc-600">
                          {Math.max(1, Math.round((f.size || 0) / 1024))} KB
                        </span>
                      </div>
                    </a>
                  );
                })}
              </div>
            </div>
          )}

          <div className="mt-6">
            <div className={micro}>Office notes</div>
            <textarea
              data-testid="lead-detail-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              onBlur={() => notes !== (lead.notes || "") && patch({ notes })}
              rows={3}
              placeholder="What you agreed, who is chasing it…"
              className="mt-2 w-full rounded-lg border border-zinc-800 bg-zinc-950 p-3 text-sm text-zinc-200 outline-none focus:border-indigo-500"
            />
          </div>

          <DraftReply leadId={id} email={lead.email} />
        </div>
      </main>
    </div>
  );
}
